import { getToken } from '../auth/tokenStore';
import {
  ApiError,
  Template,
  TemplateBlock,
  TemplateBlockCreate,
  TemplateBlockServer
} from '../common/types';
import { apiBaseUrl } from '../index';
import { convertToClientRepresentation } from './templateBlockClient';

// Retrieve all templates
export const getTemplates = async () => {
  const response = await fetch(apiBaseUrl + '/templates', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await getToken()}`
    }
  });
  if (!response.ok) {
    throw new Error(`${await response.text()}`);
  }
  return await response.json();
};

// Get a template and its associated blocks by template Id
export const getTemplateAndBlocksById = async (templateId: number) => {
  const response = await fetch(apiBaseUrl + '/templates/' + templateId, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await getToken()}`
    }
  });

  if (!response.ok) {
    throw new ApiError(await response.text(), response.status);
  }

  const { template, blocks } = await response.json();

  // for blocks with optArgs == null, set to undefined for consistency
  const clientBlocks = blocks.map((serverBlock: TemplateBlockServer) =>
    convertToClientRepresentation(serverBlock)
  );

  // Sort blocks
  clientBlocks.sort((a: TemplateBlock, b: TemplateBlock) => a.position! - b.position!);

  return { template, blocks: clientBlocks };
};

// Get a template and its associated blocks by template shareSlug
// This is a public endpoint, no authentication required
export const getTemplateAndBlocksByShareSlug = async (shareSlug: string) => {
  const response = await fetch(apiBaseUrl + '/templates/public/' + shareSlug, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new ApiError(await response.text(), response.status);
  }

  const { template, blocks } = await response.json();

  // for blocks with optArgs == null, set to undefined for consistency
  const clientBlocks = blocks.map((serverBlock: TemplateBlockServer) =>
    convertToClientRepresentation(serverBlock)
  );

  // Sort blocks
  clientBlocks.sort((a: TemplateBlock, b: TemplateBlock) => a.position! - b.position!);

  return { template, blocks: clientBlocks };
};

// Create a new template and its corresponding blocks
export const createTemplateAndBlocks = async ({
  templateData,
  templateBlockData
}: {
  templateData: Template;
  templateBlockData: TemplateBlockCreate[];
}) => {
  // API request to create template
  const templateResponse = await fetch(apiBaseUrl + '/templates', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await getToken()}`
    },
    body: JSON.stringify(templateData)
  });

  // Check if the template creation was successful
  if (!templateResponse.ok) {
    throw new Error(`${await templateResponse.text()}`);
  }

  const template = await templateResponse.json();
  const templateId = template.id;

  // Attach blocks to the template
  const blockPromises = templateBlockData.map(async (block: TemplateBlockCreate, index: number) => {
    // Translate block into expected server-side shape
    // Don't unpack block, because it has an id and other fields that we don't need
    // TODO: marry client- and server-side representations of TemplateBlock
    const serverSideBlock = {
      question: block.question,
      position: index,
      type: block.type,
      optArgs: block.optArgs
    };

    // Create the block and return its response
    const blockResponse = await fetch(
      apiBaseUrl + '/templates/' + templateId + '/template_blocks',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getToken()}`
        },
        body: JSON.stringify(serverSideBlock)
      }
    );

    // Check if the block creation was successful
    if (!templateResponse.ok) {
      throw new Error(`${await blockResponse.text()}`);
    }

    return blockResponse.json();
  });

  // Wait for all block creation requests to complete
  const blockResponses = await Promise.all(blockPromises);

  // Handle responses
  // For simplicity, log the responses here
  console.log('Template created:', template);
  blockResponses.forEach((blockResponse, index) => {
    console.log(`Block ${index + 1} created:`, blockResponse);
  });

  // Optionally, return any relevant data or identifiers
  return { templateId };
};

export const updateTemplateById = async (templateId: number, templateTitle: string) => {
  const response = await fetch(apiBaseUrl + '/templates/' + templateId, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await getToken()}`
    },
    body: JSON.stringify({ title: templateTitle })
  });
  if (!response.ok) {
    throw new Error(`${await response.text()}`);
  }
  return await response.json();
};

// Toggle the visibility of a template by setting the public status to True | False
export const toggleTemplateVisiblity = async (templateId: number, status: boolean) => {
  const response = await fetch(apiBaseUrl + '/templates/' + templateId, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await getToken()}`
    },
    body: JSON.stringify({ isPublic: status })
  });
  if (!response.ok) {
    throw new Error(`${await response.text()}`);
  }
  return await response.json();
};

// Get public template

// Delete a template and its associated template blocks by ID
export const deleteTemplateById = async (templateId: number) => {
  const response = await fetch(apiBaseUrl + '/templates/' + templateId, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await getToken()}`
    }
  });
  if (!response.ok) {
    throw new Error(`${await response.text()}`);
  }
  return await response.json();
};

// Determine if a template has been used to process a meeting/generate notes
export const getIsTemplateUsed = async (templateId: number) => {
  const response = await fetch(apiBaseUrl + '/templates/' + templateId + '/used', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await getToken()}`
    }
  });
  if (!response.ok) {
    throw new Error(`${await response.text()}`);
  }
  return await response.json();
};

export const copyTemplateByShareSlug = async (shareSlug: string): Promise<Template> => {
  const response = await fetch(apiBaseUrl + '/templates/' + shareSlug + '/copy', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await getToken()}`
    }
  });
  if (!response.ok) {
    throw new Error(`${await response.text()}`);
  }
  return await response.json();
};

export const duplicateTemplateById = async (templateId: number): Promise<Template> => {
  const response = await fetch(apiBaseUrl + '/templates/' + templateId + '/duplicate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await getToken()}`
    }
  });
  if (!response.ok) {
    throw new Error(`${await response.text()}`);
  }
  return await response.json();
};
